import { useState, useEffect } from 'react';
import { useTicketAttachments } from '../../hooks/useTicketAttachments';
import { useTicketComments } from '../../hooks/useTicketComments';
import { formatProfilePictureUrl } from '../../utils/imageUtils';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult
} from '@hello-pangea/dnd';

import { Ticket } from '../../types/ticket';
import { useAuthContext } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import TicketStatusBadge from './TicketStatusBadge';
import TicketPriorityBadge from './TicketPriorityBadge';
import { Priority, Status } from '../../types';
import { useTranslation } from 'react-i18next';

interface TicketKanbanBoardProps {
  tickets: Ticket[]
  currentPage: number
  onUpdateTicketStatus: (ticketId: number, newStatus: Status, userId: number) => void
  onTicketClick: (ticketId: number) => void
}

type ColumnType = {
  id: string;
  title: string;
  tickets: Ticket[];
};

// Status translations will be handled dynamically via i18n

export default function TicketKanbanBoard({
  tickets,
  // currentPage is kept in props for API consistency but not used directly in this component
  onUpdateTicketStatus,
  onTicketClick,
}: TicketKanbanBoardProps) {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { theme } = useTheme();
  const { attachmentCounts } = useTicketAttachments(tickets);
  const { commentCounts } = useTicketComments(tickets);

  // 1) Group tickets by status
  const [columns, setColumns] = useState<ColumnType[]>([]);
  // 2) Track if user is currently dragging
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const statusKeys: Status[] = ['new', 'processing', 'resolved', 'closed'];
    const groupedColumns = statusKeys.map((status) => ({
      id: status,
      title: t(`tickets.status.${status}`),
      tickets: tickets.filter((ticket) => ticket.status === status),
    })) as ColumnType[];
    setColumns(groupedColumns);
  }, [tickets, t]);

  // Called when a drag starts
  const onDragStart = () => {
    setIsDragging(true);
  };

  // Called when a drag ends
  const onDragEnd = (result: DropResult) => {
    setIsDragging(false); // reset drag state

    const { destination, source, draggableId } = result;
    // If no valid destination or same spot
    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const ticketId = parseInt(draggableId, 10);

    // Remove ticket from source column
    const sourceColumnIndex = columns.findIndex(
      (col) => col.id === source.droppableId
    );
    const destColumnIndex = columns.findIndex(
      (col) => col.id === destination.droppableId
    );

    const sourceColumn = columns[sourceColumnIndex];
    const destColumn = columns[destColumnIndex];

    const [movedTicket] = sourceColumn.tickets.splice(source.index, 1);
    destColumn.tickets.splice(destination.index, 0, movedTicket);

    // Update columns in state
    const updatedColumns = [...columns];
    updatedColumns[sourceColumnIndex] = sourceColumn;
    updatedColumns[destColumnIndex] = destColumn;
    setColumns(updatedColumns);

    // If the ticket actually moved to a new status, notify parent
    if (source.droppableId !== destination.droppableId) {
      // Ensure user is defined before calling onUpdateTicketStatus
      if (user) {
        onUpdateTicketStatus(ticketId, destColumn.id as Status, user.id);
      }
    }
  };

  // Navigation is now handled by the parent component through onTicketClick prop

  return (
    <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {columns.map((column) => (
          <Droppable droppableId={column.id} key={column.id}>
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`p-4 rounded-md min-h-[500px] ${
                  theme === 'dark'
                    ? snapshot.isDraggingOver ? 'bg-gray-700' : 'bg-gray-800'
                    : snapshot.isDraggingOver ? 'bg-gray-100' : 'bg-gray-50'
                }`}
              >
                <h2 className={`font-bold mb-2 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                  {column.title}
                </h2>

                {column.tickets.map((ticket, index) => {
                  const isAssignedToMe = ticket.assigned_to_id === user?.id;

                  return (
                    <Draggable
                      key={ticket.id}
                      draggableId={ticket.id.toString()}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`relative mb-4 rounded-lg p-4 border transition-all
                            hover:shadow-md
                            ${theme === 'dark'
                              ? isAssignedToMe
                                ? 'bg-gray-800 border-blue-500 shadow-sm'
                                : 'bg-gray-800 border-gray-700'
                              : isAssignedToMe
                                ? 'bg-white border-blue-500 shadow-sm'
                                : 'bg-white border-gray-200'
                            }
                            ${snapshot.isDragging ? 'shadow-2xl' : ''}
                          `}
                          onClick={() => {
                            if (!isDragging) {
                              onTicketClick(ticket.id);
                            }
                          }}
                        >
                          {/* Pin or Clip for Assigned Tickets */}
                          {isAssignedToMe && (
                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-20">
                              <div className={`w-4 h-8 relative ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                                <div className="absolute inset-x-0 top-0 bottom-0">
                                  <div className={`absolute inset-0 border-2 rounded-full transform rotate-45 ${
                                    theme === 'dark' ? 'border-blue-400' : 'border-blue-600'
                                  }`} style={{ borderRadius: '40% 40% 0 0' }}></div>
                                  <div className={`absolute inset-0 border-2 rounded-full transform rotate-45 scale-75 translate-y-0.5 ${
                                    theme === 'dark' ? 'border-blue-400' : 'border-blue-600'
                                  }`} style={{ borderRadius: '40% 40% 0 0' }}></div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Ticket Header + Dedicated Drag Handle */}
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <div className="flex items-center space-x-2">
                                <span className={`text-xs font-medium ${
                                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                }`}>#{ticket.id}</span>
                                <h3 className={`font-bold text-sm md:text-base line-clamp-1 ${
                                  theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                                }`}>
                                  {ticket.title}
                                </h3>
                              </div>
                              <div className="flex gap-2 mt-1 items-center">
                                <TicketStatusBadge status={ticket.status as Status} />
                                <TicketPriorityBadge priority={ticket.priority as Priority} />
                              </div>
                            </div>

                            {/* DRAG HANDLE */}
                            <div
                              {...provided.dragHandleProps}
                              className={`cursor-grab active:cursor-grabbing p-2 ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                              }`}
                              title={t('tickets.kanban.dragToReorder')}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <i className="fas fa-grip-lines"></i>
                            </div>
                          </div>

                          {/* Ticket Body */}
                          <p className={`text-xs md:text-sm line-clamp-3 mb-2 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            {ticket.description}
                          </p>

                          {/* Customer Info */}
                          <div className={`flex items-center gap-3 p-2 rounded-lg border ${
                            theme === 'dark'
                              ? 'bg-gray-700/50 border-gray-600'
                              : 'bg-gray-50 border-gray-100'
                          }`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${
                              theme === 'dark'
                                ? 'bg-blue-900/50 text-blue-300 ring-1 ring-blue-400/50'
                                : 'bg-blue-100 text-blue-600'
                            }`}>
                              {ticket['customer.full_name']?.[0]?.toUpperCase() || '?'}
                            </div>
                            <div>
                              <p className={`text-xs md:text-sm font-medium ${
                                theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                              }`}>
                                {ticket['customer.full_name']}
                              </p>
                              {ticket['customer.phone'] && (
                                <p className={`text-xs ${
                                  theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                                }`}>
                                  {ticket['customer.phone']}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Tags */}
                          <div className="flex flex-wrap gap-1 mt-2">
                            {ticket.source_name && (
                              <span className={`px-2 py-0.5 text-xs rounded-full ${
                                theme === 'dark'
                                  ? 'bg-purple-900/30 text-purple-400'
                                  : 'bg-purple-50 text-purple-700'
                              }`}>
                                {ticket.source_name}
                              </span>
                            )}
                            {ticket.category_name && (
                              <span className={`px-2 py-0.5 text-xs rounded-full ${
                                theme === 'dark'
                                  ? 'bg-indigo-900/30 text-indigo-400'
                                  : 'bg-indigo-50 text-indigo-700'
                              }`}>
                                {ticket.category_name}
                              </span>
                            )}
                            {!ticket.is_public && (
                              <span className={`px-2 py-0.5 text-xs rounded-full ${
                                theme === 'dark'
                                  ? 'bg-yellow-900/30 text-yellow-400'
                                  : 'bg-yellow-50 text-yellow-700'
                              }`}>
                                {t('tickets.private')}
                              </span>
                            )}
                            {/* Always show attachment icon, with count if available */}
                            <span className={`px-2 py-0.5 text-xs rounded-full flex items-center gap-1 ${
                              theme === 'dark'
                                ? 'bg-teal-900/30 text-teal-400'
                                : 'bg-teal-50 text-teal-700'
                            }`}>
                              <i className="fas fa-paperclip"></i>
                              {attachmentCounts[ticket.id] || '0'}
                            </span>
                            
                            {/* Comment count */}
                            <span className={`px-2 py-0.5 text-xs rounded-full flex items-center gap-1 ${
                              theme === 'dark'
                                ? 'bg-blue-900/30 text-blue-400'
                                : 'bg-blue-50 text-blue-700'
                            }`}>
                              <i className="fas fa-comment"></i>
                              {commentCounts[ticket.id] || '0'}
                            </span>
                          </div>

                          {/* Footer */}
                          <div className={`flex items-center justify-between text-xs pt-3 mt-2 border-t ${
                            theme === 'dark' ? 'border-gray-700' : 'border-gray-100'
                          }`}>
                            <div className="flex items-center gap-4">
                              {ticket.created_by_id && (
                                <div
                                  className={`h-6 w-6 rounded-full flex items-center justify-center font-semibold text-xs overflow-hidden ${
                                    theme === 'dark'
                                      ? 'bg-green-900/50 text-green-300 ring-1 ring-green-400/50'
                                      : 'bg-green-100 text-green-600'
                                  }`}
                                  title={`Created by: ${
                                    ticket['createdBy.fullName'] || 'Unknown'
                                  }`}
                                >
                                  {/* Profile picture with fallback to initials */}
                                  {(ticket['createdBy.pfp_url'] || (ticket.created_by && ticket.created_by.pfp_url)) ? (
                                    <img 
                                      src={formatProfilePictureUrl(ticket['createdBy.pfp_url'] || (ticket.created_by && ticket.created_by.pfp_url) || '')}
                                      alt={ticket['createdBy.fullName'] || (ticket.created_by && ticket.created_by.full_name) || 'Unknown'}
                                      className="h-full w-full object-cover"
                                      onError={(e) => {
                                        // Fallback to initials if image fails to load
                                        const imgElement = e.target as HTMLImageElement;
                                        imgElement.style.display = 'none';
                                        const nextElement = imgElement.nextElementSibling as HTMLElement;
                                        if (nextElement) {
                                          nextElement.style.display = 'flex';
                                        }
                                      }}
                                    />
                                  ) : null}
                                  <div 
                                    className={`${(ticket['createdBy.pfp_url'] || (ticket.created_by && ticket.created_by.pfp_url)) ? 'hidden' : 'flex'} h-full w-full items-center justify-center`}
                                  >
                                    <span>
                                      {(ticket['createdBy.fullName'] || 
                                        (ticket.created_by && ticket.created_by.full_name) || 
                                        'U')[0].toUpperCase()}
                                    </span>
                                  </div>
                                </div>
                              )}
                              <div className="flex flex-col">
                                <span className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}>{t('tickets.created')}</span>
                                <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {new Date(ticket.created_at).toLocaleString(undefined, {
                                    month: 'short',
                                    day: 'numeric',
                                  })}
                                </span>
                                <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {new Date(ticket.created_at).toLocaleString(undefined, {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </span>
                              </div>
                              <div className="flex flex-col">
                                <span className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}>{t('tickets.updated')}</span>
                                <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {new Date(ticket.updated_at).toLocaleString(undefined, {
                                    month: 'short',
                                    day: 'numeric',
                                  })}
                                </span>
                                <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {new Date(ticket.updated_at).toLocaleString(undefined, {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </span>
                              </div>
                            </div>
                            {ticket.assigned_to_id && (
                              <div
                                className={`h-6 w-6 rounded-full bg-blue-500 relative overflow-hidden ${ticket.assigned_to ? '' : 'opacity-50'}`}
                                title={`Assigned to: ${
                                  ticket['assignedTo.fullName'] || 'Unknown'
                                }`}
                              >
                                {/* Profile picture with fallback to initials */}
                                {(ticket['assignedTo.pfp_url'] || (ticket.assigned_to && ticket.assigned_to.pfp_url)) ? (
                                  <img 
                                    src={formatProfilePictureUrl(ticket['assignedTo.pfp_url'] || (ticket.assigned_to && ticket.assigned_to.pfp_url) || '')}
                                    alt={ticket['assignedTo.fullName'] || (ticket.assigned_to && ticket.assigned_to.full_name) || 'Unknown'}
                                    className="h-full w-full object-cover"
                                    onError={(e) => {
                                      // Fallback to initials if image fails to load
                                      const imgElement = e.target as HTMLImageElement;
                                      imgElement.style.display = 'none';
                                      const nextElement = imgElement.nextElementSibling as HTMLElement;
                                      if (nextElement) {
                                        nextElement.style.display = 'flex';
                                      }
                                    }}
                                  />
                                ) : null}
                                <div 
                                  className={`${(ticket['assignedTo.pfp_url'] || (ticket.assigned_to && ticket.assigned_to.pfp_url)) ? 'hidden' : 'flex'} h-full w-full items-center justify-center bg-blue-500 text-xs text-white`}
                                >
                                  <span>
                                    {(ticket['assignedTo.fullName'] || 
                                      (ticket.assigned_to && ticket.assigned_to.full_name) || 
                                      'U')[0].toUpperCase()}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                
                {/* Required placeholder for Droppable area */}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        ))}
      </div>
    </DragDropContext>
  );
}
